import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// FEAT-043-admin-web (MVP-8.9, MVP-18.24).
// Mandatory read-reason prompt. The panel does NOT pre-load any score
// data; this dialog gates the read. Server-side validation (`api/src/
// validation/trustScoreSchemas.ts`) requires reason length >= 10 chars
// — we mirror that here so the admin gets immediate feedback rather
// than a 400.

export const MIN_REASON_CHARS = 10;
export const MAX_REASON_CHARS = 500;

export interface TrustScoreReadReasonDialogProps {
  open: boolean;
  targetUserName: string;
  submitting?: boolean;
  errorMessage?: string | null;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}

export const TrustScoreReadReasonDialog: React.FC<TrustScoreReadReasonDialogProps> = ({
  open,
  targetUserName,
  submitting = false,
  errorMessage = null,
  onSubmit,
  onCancel,
}) => {
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_REASON_CHARS;
  const isValid = trimmed.length >= MIN_REASON_CHARS && trimmed.length <= MAX_REASON_CHARS;

  const handleSubmit = () => {
    if (!isValid || submitting) return;
    onSubmit(trimmed);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="trust-score-read-reason-title"
    >
      <DialogTitle id="trust-score-read-reason-title">
        Trust Score read — reason required
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            Trust Score reads are audit-logged. Enter a brief reason for accessing
            <Typography component="span" sx={{ fontWeight: 600 }}>
              {' '}{targetUserName}
            </Typography>
            &rsquo;s trust signals. The reason is stored against your admin id and
            this user&rsquo;s record.
          </DialogContentText>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <TextField
            label="Reason for read"
            placeholder="e.g. Investigating moderation queue report MOD-1234"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            required
            disabled={submitting}
            inputProps={{
              maxLength: MAX_REASON_CHARS,
              'data-testid': 'trust-score-reason-input',
            }}
            error={tooShort}
            helperText={
              tooShort
                ? `Reason must be at least ${MIN_REASON_CHARS} characters.`
                : `${trimmed.length}/${MAX_REASON_CHARS} characters · minimum ${MIN_REASON_CHARS}`
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting} data-testid="trust-score-reason-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          data-testid="trust-score-reason-submit"
        >
          {submitting ? 'Opening…' : 'Open Trust Score'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrustScoreReadReasonDialog;
